import React, {Component} from 'react';
import {Input} from "reactstrap";
import {withTranslation} from 'react-i18next';
import {ITEM_ALL_CONVERSIONS} from "./ActionsMultiSelect";
import {FACEBOOK_CHANNEL} from "../channels/channels";
import {
    CATEGORY_POPULAR,
    CATEGORY_ORDER,
    CATEGORY_I18N_KEYS,
    CATEGORY_FILTERS,
    groupActionsByCategory,
    isPopularAction,
    getCategoryForAction,
    stripActionPrefix,
    getTranslatedName,
    getHelpTooltip,
} from "./actionCategories";


class CategorizedActionsSelectClass extends Component {
    constructor(props) {
        super(props);

        const selectedIds = this._getInitialSelectedIds();
        const expandedCategories = this._getInitialExpandedCategories(selectedIds);

        this.state = {
            selectedIds,
            expandedCategories,
            searchText: '',
            activeFilterId: null,
        };

        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleItemChange = this.handleItemChange.bind(this);
        this.handleAllConversionsChange = this.handleAllConversionsChange.bind(this);
    }

    // --- Inicialización ---

    _getInitialSelectedIds() {
        const ids = (this.props.selectedItemIds || []).map(id => id.toString());
        if (ids.length === 0 && this.props.hasAllPrimaryConversionsOption) {
            return [ITEM_ALL_CONVERSIONS.id.toString()];
        }
        return ids;
    }

    _getInitialExpandedCategories(selectedIds) {
        if (!this._shouldCategorize()) return {};

        const items = this._getMappedItems();
        const expanded = {};

        CATEGORY_ORDER.forEach(cat => {
            expanded[cat] = false;
        });
        expanded[CATEGORY_POPULAR] = true; // Populares siempre expandido por defecto
        expanded['custom'] = false;

        // Expandir categorías que tienen items seleccionados
        items.forEach(item => {
            if (selectedIds.includes(item.id)) {
                const category = item.raw.is_custom ? 'custom' : (getCategoryForAction(item.raw.action_id) || 'other');
                expanded[category] = true;
            }
        });

        // Si ninguna categoría está expandida, expandir la primera que tenga items
        const anyExpanded = Object.values(expanded).some(v => v);
        if (!anyExpanded) {
            const {categorized} = this._getGroupedItems();
            for (const cat of CATEGORY_ORDER) {
                if (categorized[cat] && categorized[cat].length > 0) {
                    expanded[cat] = true;
                    break;
                }
            }
        }

        return expanded;
    }

    _getMappedItems() {
        const itemDataMap = this.props.itemDataMap || ((item) => ({
            name: item.name,
            id: item.id != null ? item.id.toString() : null,
        }));
        return (this.props.items || []).map(rawItem => ({
            ...itemDataMap(rawItem),
            raw: rawItem,
        }));
    }

    _shouldCategorize() {
        return this.props.channel === FACEBOOK_CHANNEL;
    }

    _getGroupedItems() {
        const itemDataMap = this.props.itemDataMap || ((item) => ({
            name: item.name,
            id: item.id != null ? item.id.toString() : null,
        }));
        return groupActionsByCategory(this.props.items || [], itemDataMap);
    }

    _isAllConversionsSelected() {
        return this.state.selectedIds.includes(ITEM_ALL_CONVERSIONS.id.toString());
    }

    // --- Handlers ---

    handleSearchChange(event) {
        this.setState({searchText: event.target.value});
    }

    handleItemChange(event) {
        const isChecked = event.target.checked;
        const itemId = event.target.dataset.id;
        let selectedIds;

        if (isChecked) {
            selectedIds = this.state.selectedIds
                .filter(id => id !== ITEM_ALL_CONVERSIONS.id.toString())
                .concat([itemId]);
        } else {
            selectedIds = this.state.selectedIds.filter(id => id !== itemId);
            if (selectedIds.length === 0 && this.props.hasAllPrimaryConversionsOption) {
                selectedIds = [ITEM_ALL_CONVERSIONS.id.toString()];
            }
        }

        this._setSelectedIds(selectedIds);
    }

    handleAllConversionsChange(event) {
        const isChecked = event.target.checked;
        if (isChecked) {
            this._setSelectedIds([ITEM_ALL_CONVERSIONS.id.toString()]);
        } else {
            // No se puede desmarcar "todas las primarias" sin seleccionar algo
            this._setSelectedIds([ITEM_ALL_CONVERSIONS.id.toString()]);
        }
    }

    handleFilterClick(filterId) {
        this.setState(prevState => ({
            activeFilterId: prevState.activeFilterId === filterId ? null : filterId,
        }));
    }

    _getActiveFilter() {
        if (!this.state.activeFilterId) return null;
        return CATEGORY_FILTERS.find(f => f.id === this.state.activeFilterId) || null;
    }

    _itemPassesFilter(item) {
        const filter = this._getActiveFilter();
        if (!filter) return true;
        const bare = stripActionPrefix(item.raw.action_id);
        return filter.filterActionIds.has(bare);
    }

    handleToggleCategory(category) {
        this.setState(prevState => ({
            expandedCategories: {
                ...prevState.expandedCategories,
                [category]: !prevState.expandedCategories[category],
            }
        }));
    }

    _setSelectedIds(selectedIds) {
        this.setState({selectedIds}, () => {
            const actionsIds = selectedIds.filter(id => id !== ITEM_ALL_CONVERSIONS.id.toString());
            this.props.onChange(actionsIds);
        });
    }

    _isSelected(itemId) {
        return this.state.selectedIds.includes(itemId);
    }

    _matchesSearch(item) {
        if (!this.state.searchText) return true;
        const query = this.state.searchText.toLowerCase();
        if (item.name.toLowerCase().includes(query)) return true;
        const translated = getTranslatedName(item.raw.action_id);
        return translated ? translated.toLowerCase().includes(query) : false;
    }

    _countSelectedInCategory(items) {
        return items.filter(item => this._isSelected(item.id)).length;
    }

    // --- Formato de conteos ---

    _formatCount(n) {
        if (n == null || n === 0) return '0';
        n = Math.round(n);
        if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
        if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
        return n.toString();
    }

    _renderCounts(counts) {
        if (!counts) return null;
        const {yesterday, last_7_days, last_30_days} = counts;
        if (!yesterday && !last_7_days && !last_30_days) return null;

        const {t} = this.props;
        const periods = [
            {label: '1d', value: yesterday, tooltip: t('categorized_actions.count_yesterday')},
            {label: '7d', value: last_7_days, tooltip: t('categorized_actions.count_last_7_days')},
            {label: '30d', value: last_30_days, tooltip: t('categorized_actions.count_last_30_days')},
        ];

        return (
            <span className="action-counts">
                {periods.map(p => (
                    <span
                        key={p.label}
                        className={`action-count-segment ${p.value ? '-has-data' : '-no-data'}`}
                        title={`${p.tooltip}: ${p.value || 0}`}
                    >
                        <span className="action-count-label">{p.label}</span>
                        <span className="action-count-value">{this._formatCount(p.value)}</span>
                    </span>
                ))}
            </span>
        );
    }

    // --- Render ---

    _renderSearch() {
        const {t} = this.props;
        return (
            <div className="categorized-actions-search">
                <input
                    type="text"
                    placeholder={t('categorized_actions.search_placeholder')}
                    value={this.state.searchText}
                    onChange={this.handleSearchChange}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                />
            </div>
        );
    }

    _renderFilters() {
        const {t} = this.props;
        if (!this._shouldCategorize()) return null;

        return (
            <div className="categorized-actions-presets">
                <span className="presets-label">{t('categorized_actions.filter_by_category')}:</span>
                <div className="presets-pills">
                    {CATEGORY_FILTERS.map(filter => {
                        const isActive = this.state.activeFilterId === filter.id;
                        return (
                            <button
                                key={filter.id}
                                type="button"
                                className={`preset-pill ${isActive ? '-active' : ''}`}
                                onClick={() => this.handleFilterClick(filter.id)}
                            >
                                {t(filter.i18nKey)}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    _renderAllConversionsOption() {
        const {t} = this.props;
        if (!this.props.hasAllPrimaryConversionsOption) return null;

        return (
            <div className="categorized-actions-all-conversions">
                <label className="multi-select-item">
                    <Input
                        type="checkbox"
                        checked={this._isAllConversionsSelected()}
                        onChange={this.handleAllConversionsChange}
                        data-id={ITEM_ALL_CONVERSIONS.id.toString()}
                    />
                    <p className="multi-select-item-name">
                        {t('actions_multi_select.all_primary_conversions')}
                    </p>
                </label>
            </div>
        );
    }

    _renderItem(item, category) {
        const isPopular = category && (category === CATEGORY_POPULAR || isPopularAction(item.raw.action_id, category));
        const {t} = this.props;
        const translated = getTranslatedName(item.raw.action_id);
        const helpText = getHelpTooltip(item.raw.action_id);
        const displayName = translated || item.name;

        return (
            <li key={item.id}>
                <label className="multi-select-item">
                    <Input
                        type="checkbox"
                        checked={this._isSelected(item.id)}
                        onChange={this.handleItemChange}
                        data-id={item.id}
                        name={item.name}
                    />
                    <div className="multi-select-item-content">
                        <p className="multi-select-item-name">{displayName}</p>
                    </div>
                    {this._renderCounts(item.raw.counts)}
                    {isPopular && <span className="popular-badge">&#9733; {t('categorized_actions.popular')}</span>}
                    {helpText && (
                        <span className="help-tooltip-container">
                            <span className="help-tooltip-icon">?</span>
                            <span className="help-tooltip-body">{helpText}</span>
                        </span>
                    )}
                </label>
            </li>
        );
    }

    _renderCategorySection(category, items) {
        const {t} = this.props;
        const isSearching = !!this.state.searchText;
        const isFiltering = !!this.state.activeFilterId;
        const filteredItems = items.filter(item =>
            this._matchesSearch(item) && this._itemPassesFilter(item)
        );

        if (filteredItems.length === 0) return null;

        const isExpanded = isSearching || isFiltering || this.state.expandedCategories[category];
        const selectedCount = this._countSelectedInCategory(items);
        const categoryName = t(CATEGORY_I18N_KEYS[category]);

        return (
            <div key={category} className="categorized-actions-category">
                <div
                    className={`category-header ${isExpanded ? '-expanded' : ''}`}
                    onClick={() => this.handleToggleCategory(category)}
                >
                    <span className="category-arrow">{isExpanded ? '\u25BC' : '\u25B6'}</span>
                    <span className="category-name">{categoryName}</span>
                    {selectedCount > 0 && (
                        <span className="category-count">
                            ({selectedCount} {selectedCount === 1 ? t('categorized_actions.selected_singular') : t('categorized_actions.selected_plural')})
                        </span>
                    )}
                </div>
                {isExpanded && (
                    <ul className="category-items">
                        {filteredItems.map(item => this._renderItem(item, category))}
                    </ul>
                )}
            </div>
        );
    }

    _renderCustomSection(customItems) {
        const {t} = this.props;
        if (customItems.length === 0) return null;

        // Cuando hay un filtro de categoría activo, ocultar custom events
        if (this.state.activeFilterId) return null;

        const isSearching = !!this.state.searchText;
        const filteredItems = customItems.filter(item => this._matchesSearch(item));

        if (filteredItems.length === 0) return null;

        const isExpanded = isSearching || this.state.expandedCategories['custom'];
        const selectedCount = this._countSelectedInCategory(customItems);

        return (
            <div className="categorized-actions-category -custom">
                <div
                    className={`category-header ${isExpanded ? '-expanded' : ''}`}
                    onClick={() => this.handleToggleCategory('custom')}
                >
                    <span className="category-arrow">{isExpanded ? '\u25BC' : '\u25B6'}</span>
                    <span className="category-name">{t('categorized_actions.custom_events')}</span>
                    {selectedCount > 0 && (
                        <span className="category-count">
                            ({selectedCount} {selectedCount === 1 ? t('categorized_actions.selected_singular') : t('categorized_actions.selected_plural')})
                        </span>
                    )}
                </div>
                {isExpanded && (
                    <ul className="category-items">
                        {filteredItems.map(item => this._renderItem(item, null))}
                    </ul>
                )}
            </div>
        );
    }

    _renderCategorizedView() {
        const {categorized, custom} = this._getGroupedItems();

        return (
            <div className="categorized-actions-body">
                {CATEGORY_ORDER.map(cat => {
                    if (cat === CATEGORY_POPULAR) {
                        const popularSection = categorized[cat] && categorized[cat].length > 0
                            ? this._renderCategorySection(cat, categorized[cat])
                            : null;
                        const customSection = this._renderCustomSection(custom);
                        if (!popularSection && !customSection) return null;
                        return (
                            <React.Fragment key="popular-and-custom">
                                {popularSection}
                                {customSection}
                            </React.Fragment>
                        );
                    }
                    if (!categorized[cat] || categorized[cat].length === 0) return null;
                    return this._renderCategorySection(cat, categorized[cat]);
                })}
            </div>
        );
    }

    _renderSimpleList() {
        const items = this._getMappedItems();
        const filteredItems = items.filter(item => this._matchesSearch(item));

        // Ordenar: mayor cantidad de eventos (30d) primero, luego alfabético
        filteredItems.sort((a, b) => {
            const countA = (a.raw.counts && a.raw.counts.last_30_days) || 0;
            const countB = (b.raw.counts && b.raw.counts.last_30_days) || 0;
            if (countB !== countA) return countB - countA;
            return a.name.localeCompare(b.name);
        });

        return (
            <div className="categorized-actions-body">
                <ul className="category-items -flat">
                    {filteredItems.map(item => this._renderItem(item, null))}
                </ul>
            </div>
        );
    }

    render() {
        return (
            <div className="categorized-actions-select">
                {this._renderSearch()}
                {this._renderFilters()}
                {this._renderAllConversionsOption()}
                {this._shouldCategorize()
                    ? this._renderCategorizedView()
                    : this._renderSimpleList()
                }
            </div>
        );
    }
}

export const CategorizedActionsSelect = withTranslation()(CategorizedActionsSelectClass);
export default CategorizedActionsSelect;
